type DownloadHeaders = Record<string, string | undefined>

type DownloadResponse = {
  body?: Blob | ArrayBuffer | Uint8Array | string
  headers?: DownloadHeaders
}

const getHeaderValue = (headers: DownloadHeaders | undefined, key: string) => {
  if (!headers) return undefined

  return (
    headers[key] ?? headers[key.toLowerCase()] ?? headers[key.toUpperCase()]
  )
}

const normalizeFileName = (value: string) => {
  return value
    .trim()
    .replace(/^"(.*)"$/, '$1')
    .replace(/^'(.*)'$/, '$1')
}

export const extractFileNameFromContentDisposition = (
  contentDisposition?: string,
  fallbackFileName = 'arquivo.xlsx'
) => {
  if (!contentDisposition) return fallbackFileName

  const parts = contentDisposition
    .split(';')
    .map(part => part.trim())
    .filter(Boolean)

  const fileNameStarPart = parts.find(part =>
    part.toLowerCase().startsWith('filename*=')
  )

  if (fileNameStarPart) {
    const rawValue = fileNameStarPart.slice(fileNameStarPart.indexOf('=') + 1)
    const cleanedValue = normalizeFileName(rawValue).replace(/^UTF-8''/i, '')

    try {
      return decodeURIComponent(cleanedValue)
    } catch {
      return cleanedValue || fallbackFileName
    }
  }

  const fileNamePart = parts.find(part =>
    part.toLowerCase().startsWith('filename=')
  )

  if (fileNamePart) {
    const rawValue = fileNamePart.slice(fileNamePart.indexOf('=') + 1)
    const cleanedValue = normalizeFileName(rawValue)

    return cleanedValue || fallbackFileName
  }

  return fallbackFileName
}

export const downloadBlobResponse = (
  response: DownloadResponse,
  fallbackFileName: string
) => {
  if (!response?.body) {
    throw new Error('Resposta vazia ao exportar arquivo.')
  }

  const contentDisposition = getHeaderValue(
    response.headers,
    'content-disposition'
  )
  const contentType =
    getHeaderValue(response.headers, 'content-type') ??
    'application/octet-stream'

  const fileName = extractFileNameFromContentDisposition(
    contentDisposition,
    fallbackFileName
  )
  const blob =
    response.body instanceof Blob
      ? response.body
      : new Blob([response.body], { type: contentType })
  const objectUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')

  link.href = objectUrl
  link.download = fileName
  link.style.display = 'none'
  document.body.appendChild(link)

  try {
    link.click()
  } finally {
    document.body.removeChild(link)
    URL.revokeObjectURL(objectUrl)
  }

  return fileName
}
